import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';
import { apiService } from '../../services/api';

/* ------------------------------------------------------------------ */
/* Typography: load Fraunces, Plus Jakarta Sans, JetBrains Mono once.  */
/* ------------------------------------------------------------------ */
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap';

function ensureFontsLoaded() {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

/* ------------------------------------------------------------------ */
/* Palette — same as Payments.tsx                                      */
/* ------------------------------------------------------------------ */
const INK = '#1a1612';
const INK_SOFT = '#4a4540';
const INK_FAINT = '#8a847d';
const PAPER = '#f4efe6';
const PAPER_RULE = 'rgba(26, 22, 18, 0.14)';
const ACCENT = '#c13a16';
const ACCENT_INK = '#7a1f07';
const SUCCESS = '#3e6e4f';

const SERIF = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const SANS = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, monospace';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
interface EmailLogItem {
  id: string;
  toEmail: string;
  subject: string;
  templateKey: string;
  status: 'sent' | 'failed' | string;
  errorMessage?: string | null;
  tenantId?: string | null;
  createdAt: string;
}

type ViewState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'error'; msg: string };

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */
const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const TEMPLATE_OPTIONS = [
  { value: '', label: 'Todos los templates' },
  { value: 'welcome', label: 'welcome' },
  { value: 'payment_succeeded', label: 'payment_succeeded' },
  { value: 'payment_failed', label: 'payment_failed' },
  { value: 'trial_ending_2d', label: 'trial_ending_2d' },
  { value: 'trial_expired', label: 'trial_expired' },
  { value: 'test', label: 'test' },
  { value: 'custom', label: 'custom' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'sent', label: 'Enviados' },
  { value: 'failed', label: 'Fallidos' },
];

/* ------------------------------------------------------------------ */
/* Visual primitives                                                   */
/* ------------------------------------------------------------------ */
const PaperBackdrop = () => (
  <>
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.5,
        mixBlendMode: 'multiply',
        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
          '<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'220\' height=\'220\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\' seed=\'3\' stitchTiles=\'stitch\'/><feColorMatrix type=\'matrix\' values=\'0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0 0.04  0 0 0 0.08 0\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>'
        )}")`,
      }}
    />
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        background: `radial-gradient(120% 85% at 50% 40%, transparent 55%, rgba(26, 22, 18, 0.08))`,
      }}
    />
  </>
);

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="p"
    sx={{
      fontFamily: MONO,
      fontSize: 11,
      letterSpacing: '0.28em',
      textTransform: 'uppercase',
      color: INK_FAINT,
    }}
  >
    {children}
  </Typography>
);

const HairlineRule = ({ label }: { label?: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
    <Box sx={{ flex: 1, height: '1px', background: PAPER_RULE }} />
    {label && (
      <Typography sx={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.24em', color: INK_FAINT, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    )}
    <Box sx={{ flex: 1, height: '1px', background: PAPER_RULE }} />
  </Box>
);

const StatusDot = ({ status }: { status: string }) => {
  const ok = status === 'sent';
  const color = ok ? SUCCESS : ACCENT;
  const label = ok ? 'Enviado' : status === 'failed' ? 'Fallido' : status;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.3,
        border: `1px solid ${color}`,
        background: ok ? 'rgba(62, 110, 79, 0.08)' : 'rgba(193, 58, 22, 0.06)',
        borderRadius: '2px',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const InkButton = ({
  onClick,
  children,
  variant = 'primary',
  disabled,
  endIcon,
  startIcon,
  type,
}: {
  onClick?: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'ghost';
  disabled?: boolean;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  type?: 'button' | 'submit';
}) => {
  const isPrimary = variant === 'primary';
  return (
    <Button
      disableRipple
      disableElevation
      onClick={onClick}
      disabled={disabled}
      endIcon={endIcon}
      startIcon={startIcon}
      type={type}
      sx={{
        fontFamily: MONO,
        fontSize: 11,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        px: isPrimary ? 3 : 2,
        py: 1.2,
        borderRadius: 0,
        minHeight: 40,
        color: isPrimary ? PAPER : INK,
        background: isPrimary ? INK : 'transparent',
        border: isPrimary ? `1px solid ${INK}` : `1px solid ${PAPER_RULE}`,
        transition: 'all 180ms ease',
        '&:hover': {
          background: isPrimary ? ACCENT_INK : 'rgba(26, 22, 18, 0.04)',
          borderColor: isPrimary ? ACCENT_INK : INK,
          color: isPrimary ? PAPER : INK,
        },
        '&.Mui-disabled': { opacity: 0.35, color: INK },
      }}
    >
      {children}
    </Button>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function Emails() {
  ensureFontsLoaded();

  const [items, setItems] = useState<EmailLogItem[]>([]);
  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [templateFilter, setTemplateFilter] = useState<string>('');

  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const api = useMemo(() => (apiService as any).api, []);

  const load = async () => {
    try {
      setView({ kind: 'loading' });
      const params: Record<string, string> = { limit: '200' };
      if (statusFilter) params.status = statusFilter;
      if (templateFilter) params.templateKey = templateFilter;
      const res = await api.get('/super-admin/emails', { params });
      setItems(res.data?.items ?? []);
      setView({ kind: 'idle' });
    } catch (e: any) {
      setView({ kind: 'error', msg: e?.response?.data?.error || 'No pudimos cargar los emails' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, templateFilter]);

  const sendTest = async () => {
    if (!testEmail.trim()) return;
    setSending(true);
    try {
      const res = await api.post('/super-admin/emails/test', { toEmail: testEmail.trim() });
      if (res.data?.sent) {
        setBanner({ tone: 'success', text: `Email de prueba enviado a ${res.data.toEmail}` });
      } else {
        setBanner({ tone: 'error', text: 'El SMTP devolvió false. Revisá el log.' });
      }
      setTestOpen(false);
      setTestEmail('');
      await load();
    } catch (e: any) {
      setBanner({ tone: 'error', text: e?.response?.data?.error || 'No pudimos enviar el email de prueba' });
    } finally {
      setSending(false);
    }
  };

  const stats = useMemo(() => {
    const sent = items.filter((i) => i.status === 'sent').length;
    const failed = items.filter((i) => i.status === 'failed').length;
    return { sent, failed, total: items.length };
  }, [items]);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: PAPER,
        color: INK,
        fontFamily: SANS,
        overflowX: 'hidden',
      }}
    >
      <PaperBackdrop />

      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 6, md: 10 } }}>
        {/* ============ HEADER ============ */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 4 }}>
            <Kicker>Correo transaccional · Auditoría</Kicker>
            <Kicker>N.º 2026 — Emails</Kicker>
          </Stack>

          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF,
              fontWeight: 350,
              fontVariationSettings: `"opsz" 144, "SOFT" 40`,
              fontSize: { xs: 54, sm: 80, md: 104 },
              lineHeight: 0.95,
              letterSpacing: '-0.035em',
              color: INK,
              mb: 2.5,
            }}
          >
            Qué <em style={{ fontStyle: 'italic', color: ACCENT, fontWeight: 360 }}>llegó</em><br />
            al buzón.
          </Typography>

          <Box sx={{ width: 110, height: '2px', background: INK, mb: 3.5 }} />

          <Typography
            component="p"
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: 17, md: 20 },
              lineHeight: 1.55,
              color: INK_SOFT,
              maxWidth: 640,
              fontWeight: 350,
            }}
          >
            Cada email transaccional que sale del sistema queda registrado acá — desde los de
            bienvenida hasta los avisos de pago rechazado. Si algo no llegó, este es el primer
            lugar donde mirar.
          </Typography>
        </motion.div>

        {banner && (
          <Box sx={{ mt: 5 }}>
            <Alert
              severity={banner.tone}
              onClose={() => setBanner(null)}
              sx={{
                borderRadius: 0,
                border: `1px solid ${banner.tone === 'success' ? SUCCESS : ACCENT}`,
                background: banner.tone === 'success' ? 'rgba(62, 110, 79, 0.08)' : 'rgba(193, 58, 22, 0.06)',
                color: INK,
                fontFamily: SANS,
                '& .MuiAlert-icon': { color: banner.tone === 'success' ? SUCCESS : ACCENT },
              }}
            >
              {banner.text}
            </Alert>
          </Box>
        )}

        {/* ============ TOOLBAR: filters + test button ============ */}
        <Box sx={{ mt: { xs: 7, md: 10 } }}>
          <HairlineRule label={`${stats.sent} enviados · ${stats.failed} fallidos · ${stats.total} totales`} />

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mt: 3, mb: 4 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <FilterSelect
                label="Estado"
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
              />
              <FilterSelect
                label="Template"
                value={templateFilter}
                onChange={setTemplateFilter}
                options={TEMPLATE_OPTIONS}
              />
            </Stack>

            <InkButton onClick={() => setTestOpen(true)} startIcon={<SendIcon sx={{ fontSize: 14 }} />}>
              Enviar email de prueba
            </InkButton>
          </Stack>
        </Box>

        {/* ============ LIST ============ */}
        {view.kind === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={28} sx={{ color: INK }} />
          </Box>
        )}

        {view.kind === 'error' && (
          <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${ACCENT}`, background: 'rgba(193, 58, 22, 0.06)' }}>
            {view.msg}
          </Alert>
        )}

        {view.kind === 'idle' && items.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 18, color: INK_FAINT }}>
              Todavía no se envió ningún email con los filtros actuales.
            </Typography>
          </Box>
        )}

        {view.kind === 'idle' && items.length > 0 && (
          <Box
            sx={{
              border: `1px solid ${PAPER_RULE}`,
              background: 'rgba(255,255,255,0.35)',
            }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: '160px 1fr 1.4fr 140px 110px 120px',
                columnGap: 2,
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${PAPER_RULE}`,
                background: 'rgba(26, 22, 18, 0.03)',
              }}
            >
              {['Fecha', 'Para', 'Asunto', 'Template', 'Estado', 'Tenant'].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    letterSpacing: '0.24em',
                    textTransform: 'uppercase',
                    color: INK_FAINT,
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {items.map((it, idx) => (
              <Box
                key={it.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '160px 1fr 1.4fr 140px 110px 120px' },
                  columnGap: 2,
                  rowGap: 1,
                  px: 2.5,
                  py: 2,
                  alignItems: 'center',
                  borderBottom: idx < items.length - 1 ? `1px solid ${PAPER_RULE}` : 'none',
                  '&:hover': { background: 'rgba(26, 22, 18, 0.02)' },
                }}
              >
                <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: INK_SOFT }}>
                  {formatDateTime(it.createdAt)}
                </Typography>
                <Typography sx={{ fontFamily: SANS, fontSize: 13, color: INK, wordBreak: 'break-all' }}>
                  {it.toEmail}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: SERIF,
                    fontSize: 14,
                    color: INK,
                    fontWeight: 400,
                    lineHeight: 1.35,
                  }}
                >
                  {it.subject}
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: INK_SOFT }}>
                  {it.templateKey}
                </Typography>
                <Box>
                  <StatusDot status={it.status} />
                  {it.status === 'failed' && it.errorMessage && (
                    <Typography
                      title={it.errorMessage}
                      sx={{
                        fontFamily: MONO,
                        fontSize: 10,
                        color: ACCENT,
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 200,
                      }}
                    >
                      {it.errorMessage}
                    </Typography>
                  )}
                </Box>
                <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: INK_FAINT, wordBreak: 'break-all' }}>
                  {it.tenantId ? String(it.tenantId).slice(0, 8) + '…' : '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ============ FOOTNOTE ============ */}
        <Box sx={{ mt: 10, pt: 5, borderTop: `1px solid ${PAPER_RULE}` }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: 13.5, fontStyle: 'italic', color: INK_FAINT, maxWidth: 620, lineHeight: 1.55 }}>
            Los envíos corren por SMTP sobre Gmail. Si un correo falló, probablemente haya un
            detalle en la columna de estado — revisá el stack trace o tirá un email de prueba
            al mismo destinatario desde este mismo panel.
          </Typography>
        </Box>
      </Box>

      {/* ============ TEST EMAIL DIALOG ============ */}
      <Dialog
        open={testOpen}
        onClose={() => !sending && setTestOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: PAPER,
            borderRadius: 0,
            border: `1px solid ${INK}`,
            boxShadow: '0 40px 80px rgba(26, 22, 18, 0.22)',
          },
        }}
      >
        <Box sx={{ position: 'relative', p: { xs: 4, md: 5 }, color: INK }}>
          <IconButton
            onClick={() => !sending && setTestOpen(false)}
            sx={{ position: 'absolute', top: 14, right: 14, color: INK, borderRadius: 0 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Kicker>Diagnóstico · SMTP</Kicker>

          <Typography
            sx={{
              fontFamily: SERIF,
              fontWeight: 340,
              fontVariationSettings: `"opsz" 144, "SOFT" 40`,
              fontSize: { xs: 32, md: 44 },
              lineHeight: 1,
              letterSpacing: '-0.03em',
              color: INK,
              mt: 1,
              mb: 1,
            }}
          >
            Enviar <em style={{ color: ACCENT, fontStyle: 'italic' }}>prueba</em>.
          </Typography>

          <Box sx={{ width: 60, height: '2px', background: INK, my: 2.5 }} />

          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: 14.5,
              fontStyle: 'italic',
              color: INK_SOFT,
              mb: 3,
              lineHeight: 1.55,
            }}
          >
            Un correo vacío con template <code style={{ fontFamily: MONO }}>test</code>. Sirve
            para verificar que el SMTP está andando y los credenciales son válidas.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            type="email"
            placeholder="destino@ejemplo.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            disabled={sending}
            variant="outlined"
            InputProps={{
              sx: {
                fontFamily: MONO,
                fontSize: 13,
                borderRadius: 0,
                background: '#fff',
                '& fieldset': { borderColor: INK },
                '&:hover fieldset': { borderColor: INK },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
            }}
            sx={{ mb: 3 }}
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <InkButton variant="ghost" onClick={() => setTestOpen(false)} disabled={sending}>
              Cancelar
            </InkButton>
            <InkButton onClick={sendTest} disabled={sending || !testEmail.trim()}>
              {sending ? 'Enviando…' : 'Enviar'}
            </InkButton>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* FilterSelect                                                         */
/* ------------------------------------------------------------------ */
function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <Stack spacing={0.5}>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 9.5,
          letterSpacing: '0.26em',
          textTransform: 'uppercase',
          color: INK_FAINT,
        }}
      >
        {label}
      </Typography>
      <Select
        value={value}
        onChange={(e) => onChange(String(e.target.value))}
        size="small"
        sx={{
          minWidth: 180,
          fontFamily: MONO,
          fontSize: 12,
          borderRadius: 0,
          background: '#fff',
          '& fieldset': { borderColor: INK },
          '&:hover fieldset': { borderColor: INK },
          '&.Mui-focused fieldset': { borderColor: ACCENT },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: MONO, fontSize: 12 }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}
